import classnames from "classnames";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighMaps from "highcharts/modules/map";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import ApiService from "../services/ApiService";

import mapSpanish from "@highcharts/map-collection/countries/es/es-all.geo.json";
import mapNorway from "@highcharts/map-collection/countries/no/no-all-all.geo.json";
import mapSweden from "@highcharts/map-collection/countries/se/se-all.geo.json";
import mapWorld from "@highcharts/map-collection/custom/world.geo.json";
import { useQuery } from "@tanstack/react-query";
import { Nav, NavItem, NavLink, Spinner } from "reactstrap";

const internationalIds = [149, 179, 203];

HighMaps(Highcharts);

// (Tenant id/name) -> map
const maps = {
  'Betsson': mapWorld,
  'GNK Dinamo Zagreb': mapWorld,
  149: mapWorld,
  178: mapSweden,
  208: mapSweden,
  160: mapSweden,
  205: mapSpanish,
  207: mapSpanish,
  204: mapSpanish,
  206: mapSpanish,
}

const getMapForTenant = (tenant) => {
  if (maps[tenant.name]) {
    return maps[tenant.name];
  }

  if (maps[tenant.id]) {
    return maps[tenant.id];
  }

  return mapNorway;
}

const Map = ({ tenant }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const isInternational = internationalIds.includes(tenant.id);

  const {
    data: sources,
    isLoading,
    isRefetching,
    isSuccess,
  } = useQuery({
    queryKey: ["followerGeography", tenant.id],
    queryFn: ApiService.getFollowerGeography,
    select: (data) => data.data.sources,
    placeholderData: { data: { sources: [] } }, // Make sure to match the correct data structure
  });

  const map = getMapForTenant(tenant);

  const chartOptions = (source) => {
    const name = source.source.match(/[a-zA-Z]+/g);
    var array = source.data;
    var obj = Object.keys(array).map((key) => [key, array[key]]);

    const isPercent = source.source.includes("%");

    const maxValue = Math.max(...obj.map((o) => o[1]));
    return {
      title: {
        text: "",
      },
      mapNavigation: {
        enabled: true,
        buttonOptions: {
          verticalAlign: "bottom",
        },
        enableDoubleClickZoomTo: true,
      },
      colorAxis: {
        maxColor: "#DDA600",
        minColor: "#F8EBC3",
        min: 0,
        max: maxValue,
        labels: {
          format: isPercent ? "{value}%" : "{value}",
          style: {
            color: "var(--text-primary)",
          },
        },
      },
      credits: { enabled: false },
      chart: {
        height: 403,
        backgroundColor: 'transparent',
      },
      series: [
        {
          tooltip: { valueSuffix: isPercent ? "%" : '' },
          type: "map",
          data: obj,
          nullColor: "#95969B",
          mapData: map,
          name: name,
          keys: [isInternational ? "iso-a2" : 'name', "value"],
          joinBy: isInternational ? "iso-a2" : 'name',
          borderWidth: 0.2,
          states: {
            hover: {
              color: "#BADA55",
            },
          },
          dataLabels: {
            enabled: true,
            color: 'var(--text-primary)',
            formatter: function () {
              if (this.point.value > 0) {
                return isPercent ? this.point.value + "%" : this.point.value;
              }
              return null;
            },
          },
        },
      ],
    };
  };

  const percentToggle = (activeTab) => {
    if (activeTab % 2 === 0) {
      return (
        <NavItem className="percent-button">
          <NavLink className="percent-link" active>
            %
          </NavLink>
          <NavLink
            className="percent-link"
            onClick={() => {
              toggleTab(prevActiveTab => prevActiveTab + 1);
            }}
          >
            {t("ant")}
          </NavLink>
        </NavItem>
      );
    } else {
      return (
        <NavItem className="percent-button">
          <NavLink
            className="percent-link"
            onClick={() => {
              toggleTab(prevActiveTab => prevActiveTab - 1);
            }}
          >
            %
          </NavLink>
          <NavLink className="percent-link" active>
            {t("ant")}
          </NavLink>
        </NavItem>
      );
    }
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const renderTab = (activeTab) => {
    return (
      <>
        <NavItem key={activeTab}>
          <NavLink
            href="#"
            className={classnames({
              active: activeTab < 2,
            })}
            onClick={() => {
              toggleTab(0);
            }}
          >
            Facebook
          </NavLink>
        </NavItem>
        <NavItem key={activeTab + 1}>
          <NavLink
            href="#"
            className={classnames({
              active: activeTab > 1 && activeTab < 4,
            })}
            onClick={() => {
              toggleTab(2);
            }}
          >
            Instagram
          </NavLink>
        </NavItem>
      </>
    );
  };

  const activeSource = sources[activeTab];

  return (
    <>
      {(isLoading || isRefetching) && (
        <Spinner
          color="primary"
          size="sm"
          style={{ position: "absolute", right: 20 }}
        />
      )}
      {isSuccess && sources && sources.length > 0 && (
        <>
          <Nav pills className="navtab-bg my-4 center-pills">
            {renderTab(activeTab)}
          </Nav>

          <div className="p-3">
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions(activeSource)}
              constructorType={"mapChart"}
            />
            </div>
          <Nav pills className="navtab-bg center-pills">
            {percentToggle(activeTab)}
          </Nav>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  tenant: state.auth.tenant,
});

export default connect(mapStateToProps)(Map);